<template>
  <div id="app" class="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
    <AppHeader />
    <main class="flex-1 pb-16 md:pb-0">
      <router-view />
    </main>
    <AppFooter class="hidden md:block" />
    <MobileBottomNav />
    
    <!-- Global Auth Modal -->
    <AuthModal
      :isOpen="showAuthModal"
      @close="showAuthModal = false"
      @success="handleAuthSuccess"
    />
    
    <!-- GDPR Consent Banner -->
    <GDPRConsentBanner />
    
    <!-- AdSense Debug Component (Development Only) -->
    <AdSenseDebug />
    
  <!-- Ad Debug Panel (Development Only - hidden by default) -->
  <AdDebugPanel v-if="showAdDebug" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, defineAsyncComponent } from 'vue';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';

// Import components
const AppHeader = defineAsyncComponent(() => import('./components/AppHeader.vue'));
const AppFooter = defineAsyncComponent(() => import('./components/AppFooter.vue'));
const MobileBottomNav = defineAsyncComponent(() => import('./components/MobileBottomNav.vue'));
const AuthModal = defineAsyncComponent(() => import('./components/AuthModal.vue'));
const GDPRConsentBanner = defineAsyncComponent(() => import('./components/GDPRConsentBanner.vue'));
const AdSenseDebug = defineAsyncComponent(() => import('./components/AdSenseDebug.vue'));
const AdDebugPanel = defineAsyncComponent(() => import('./components/AdDebugPanel.vue'));
import { initializeGlobalAuth, cleanupGlobalAuth } from './composables/useAuth';
import { useConsentStore } from './stores/consentStore';
import { appEventBus } from './utils/appEventBus';
import { admobService } from './services/admobService';
import type { AuthUser } from './firebase/services/authService';

const showAuthModal = ref(false);
// Explicitly hide ad debug panel unless you flip this to true locally
const showAdDebug = ref(false);
const consentStore = useConsentStore();

// Listen for openAuthModal events from anywhere in the app
const handleOpenAuthModal = () => {
  showAuthModal.value = true;
};

const handleAuthSuccess = (_user: AuthUser) => {
  showAuthModal.value = false;
};



// Initialize mobile platform features
const initializeMobilePlatform = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      // Configure status bar
      await StatusBar.setStyle({ style: Style.Dark });
      await StatusBar.setBackgroundColor({ color: '#1f2937' });
      
      // Hide splash screen
      await SplashScreen.hide();
      
      // Initialize AdMob
      await admobService.initialize();
      
      console.log('Mobile platform initialized');
    } catch (error) {
      console.error('Failed to initialize mobile platform:', error);
    }
  }
};

// Initialize authentication and GDPR compliance when the app mounts
onMounted(async () => {
  initializeGlobalAuth();
  appEventBus.on('openAuthModal', handleOpenAuthModal);
  
  // Initialize GDPR compliance
  await consentStore.initializeGDPR();
  
  // Initialize mobile platform features
  await initializeMobilePlatform();
});

// Cleanup authentication when the app unmounts
onUnmounted(() => {
  cleanupGlobalAuth();
  appEventBus.off('openAuthModal', handleOpenAuthModal);
});
</script>

<style>
#app {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Ensure smooth transitions for theme changes */
* {
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}
</style>
